import { useEffect, useRef, useState } from "react";
import { CustomerCard } from "@/react/features/customer/card";
import { useSelector } from "react-redux";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import Button from "@8x8/oxygen-button";
import Modal, { ModalHeader } from "@8x8/oxygen-modal";
import Input from "@8x8/oxygen-input";
import {
  useReplaceCustomerCampaignMutation,
  useRetrieveCustomerCampaignsQuery,
} from "./smsCampaignApi";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import PageLayout from "@/react/features/styled/PageLayout";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import lodash from "lodash";
import { ObjectPropertyValueContainer } from "@/react/features/styled/ObjectPropertyValueContainer";
import { ObjectPropertyLabel } from "@/react/features/styled/ObjectPropertyLabel";
import {
  CampaignButtonsContainer,
  CampaignInfoContainer,
  DetailsCardContainer,
  ReplaceModalContentContainer,
} from "./styled";

export const SmsCampaignPage = () => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const campaignIdRef = useRef();
  const customerId = useSelector(customerIdSelector);
  const isInitialMount = useRef(true);
  const {
    data: smsCampaigns,
    error: smsCampaignsError,
    isFetching,
    refetch,
  } = useRetrieveCustomerCampaignsQuery();
  const smsCampaign = smsCampaigns ? smsCampaigns[0] : null;
  const [replaceCampaign, { isLoading }] = useReplaceCustomerCampaignMutation();

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      refetch();
    }
  }, [customerId]);

  useErrorNotification(smsCampaignsError);

  const isCampaignLoading = isLoading || isFetching;

  const handleReplace = () => {
    replaceCampaign(campaignIdRef.current.value)
      .unwrap()
      .then(() => {
        setIsReplaceModalOpen(true);
        refetch();
      })
      .catch(errorNotification);
    setIsReplaceModalOpen(false);
  };

  const content = () => {
    if (!customerId) {
      return (
        <div>
          No customer selected. Please select a customer to see its SMS
          Campaign.
        </div>
      );
    }
    if (!smsCampaign) {
      return <div>There is no SMS Campaign for this customer.</div>;
    }
    return (
      <FullWidthDiv>
        <CampaignInfoContainer>
          {["campaignId", "status"].map((key) => (
            <ObjectPropertyValueContainer key={key}>
              <ObjectPropertyLabel>{lodash.startCase(key)}</ObjectPropertyLabel>
              <span>{smsCampaign[key]}</span>
            </ObjectPropertyValueContainer>
          ))}
          {smsCampaign.status === "REJECTED" && (
            <ObjectPropertyValueContainer>
              <ObjectPropertyLabel>Rejection Reason</ObjectPropertyLabel>
              <span>{smsCampaign.errorDetails}</span>
            </ObjectPropertyValueContainer>
          )}
        </CampaignInfoContainer>
        <CampaignButtonsContainer>
          <Button
            variant={"primary"}
            onClick={() => setIsReplaceModalOpen(true)}
            size={"medium"}
          >
            Replace
          </Button>
          <Button
            variant={"secondary"}
            onClick={() => setIsDetailsModalOpen(true)}
            size={"medium"}
          >
            More details
          </Button>
        </CampaignButtonsContainer>
      </FullWidthDiv>
    );
  };

  return (
    <>
      <Modal
        isVisible={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        shouldCloseOnOverlayClick
        height={"50vh"}
      >
        <ModalHeader
          title={`Campaign ${smsCampaign?.campaignId}`}
          hasBorderBottom
          onClose={() => setIsDetailsModalOpen(false)}
        />
        <DetailsCardContainer>
          {smsCampaign &&
            Object.keys(smsCampaign.payload).map((key) => (
              <ObjectPropertyValueContainer>
                <ObjectPropertyLabel>
                  {lodash.startCase(key)}
                </ObjectPropertyLabel>
                <span>
                  {smsCampaign.payload[key] === null
                    ? "-"
                    : String(smsCampaign.payload[key])}
                </span>
              </ObjectPropertyValueContainer>
            ))}
        </DetailsCardContainer>
      </Modal>

      <Modal
        isVisible={isReplaceModalOpen}
        onClose={() => setIsReplaceModalOpen(false)}
        shouldCloseOnOverlayClick
      >
        <ModalHeader
          title={`Replace Campaign`}
          hasBorderBottom
          onClose={() => setIsReplaceModalOpen(false)}
        />
        <ReplaceModalContentContainer>
          <Input
            type="text"
            placeholder={"Campaign ID"}
            inputRef={campaignIdRef}
          />
          <Button onClick={handleReplace}>Save</Button>
        </ReplaceModalContentContainer>
      </Modal>

      <PageLayout>
        <CustomerCard />
        {isCampaignLoading ? <div> Loading... </div> : content()}
      </PageLayout>
    </>
  );
};
